import React from 'react'
import { Action, IOWidget, mk_widget } from 'widgets-for-react'
import { ButtonData } from '../../../../wordpress/types_and_parser/extra_types.mod'
import { CMSState } from '../../../../cms/blocks/types.gen'
import { link, linkWrapper } from '../../../../cms/blocks/utils.gen'
import { t } from 'i18next'
import { PublicSite_CustomPageData } from '../../../application.mod'
import { useState } from 'react'
import { wysiwyg } from '../../../templates/wysiwyg.mod'
import { decodeHTMLEntities, gtmPush, MouseClickEvent } from '../../../../../helpers'

export type TextBlockProps<CustomBlockData, extra_images> = {
  title?: string
  text: string
  primary_button?: ButtonData
  secondary_button?: ButtonData
  has_show_more: boolean
}

export type TextBlockStyle = Partial<{
  block_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  content_classname: string
  text_classname: string
  footer_classname: string
  clickable_primary_classname: string
  clickable_secondary_classname: string
}>

export function TextBlock<CustomBlockData, extra_images>(
  props: TextBlockProps<CustomBlockData, extra_images>,
  options?: { style?: TextBlockStyle; key?: string }
): IOWidget<CMSState<CustomBlockData, extra_images, PublicSite_CustomPageData>, Action<CMSState<CustomBlockData, extra_images, never>>> {
  return (s: CMSState<CustomBlockData, extra_images, PublicSite_CustomPageData>) =>
    mk_widget({
      run: onDone => (
        <TextBlockComponent textProps={props} options={options} cmssState={s} />
      )
    })
}

type Props<CustomBlockData, extra_images> = {
  textProps: TextBlockProps<CustomBlockData, extra_images>
  options: { style?: TextBlockStyle; key?: string }
  cmssState: CMSState<CustomBlockData, extra_images, PublicSite_CustomPageData>
}

function TextBlockComponent<CustomBlockData, extra_images>(props: Props<CustomBlockData, extra_images>) {
  const [expanded, setExpanded] = useState(false)
  
  return (
    <section
      className={`block block--text${props.textProps.has_show_more ? (` block--show expand${expanded ? 'ed' : 'able'}`): ''}`}
      key={props.options && props.options.key ? props.options.key : 'text-block'}
    >
      <div className="block__inner container container--s gutter--m spacing--m">
      {props.textProps.title &&
        <header className="block__header">
          <h2 className="block__title typo--l ff--primary">{decodeHTMLEntities(props.textProps.title)}</h2>
        </header>
      }          

        <div className="block__content">
          { wysiwyg({ 
            className:"block__text text--styled"
          })(props.textProps.text).run(_ => {}) }
        </div>

        {props.textProps.has_show_more && 
          <div className="block__action">
            {<button 
              className={`button button--toggle${expanded ? ' button--hide' : ''}`} 
              onClick={_ => {
                gtmPush({
                  event: "text_show_more"
                })
                setExpanded(expanded ? false : true)}
              }>
              <span>{expanded ? t('show_button.less') :t('show_button.more')}</span>
              <svg className="button__icon" height="6" width="9" aria-hidden="true"><use xlinkHref="#chevron--down" /></svg>
            </button>}
          </div>
        }

        {(props.textProps.primary_button || props.textProps.secondary_button) && (
          <footer className="block__footer ls-clickables">
            {props.textProps.primary_button && (
              <div className="ls-clickable">
                {link(linkWrapper({title:props.textProps.primary_button.text,url:props.textProps.primary_button.url}),
                  { classname: 'button button--primary' },
                  props.textProps.primary_button.text, 
                  () => {
                    gtmPush({
                      event: 'text_block', 
                      cta_type: 'primary_button', 
                      click_text: props.textProps.primary_button.text
                    })
                  }
                )}
              </div>
            )}
            {props.textProps.secondary_button && (
              <div className="ls-clickable">
                {link(linkWrapper({title:props.textProps.secondary_button.text,url:props.textProps.secondary_button.url}),
                  { classname: 'button button--secondary' },
                  props.textProps.secondary_button.text, 
                  () => {
                    gtmPush({
                      event: 'text_block', 
                      cta_type: 'secondary_button', 
                      click_text: props.textProps.secondary_button.text
                    })
                  }
                )}
              </div>
            )}
          </footer>
        )}
      </div>
    </section>
  )
}